import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DatePicker, Form, Input, Select } from "antd";
import { Button } from "react-bootstrap";
import { Policy, PolicyField } from "../../models/DataSpace";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

const formLayout = {
  labelCol: {
    xs: 24,
    md: 7,
  },
  wrapperCol: {
    xs: 24,
    md: 15,
  },
}

export interface PolicyDTO {
  id?: string;
  policy: string;
  fields: PolicyField[];
}

type Props = {
  onCancel?: () => void;
  onSubmit?: (policy: PolicyDTO) => void;
  availablePolicies?: Policy[];
  policy?: PolicyDTO;
}

export default function PolicyForm({ onCancel, onSubmit, availablePolicies, policy }: Props) {
  const [form] = Form.useForm();
  const [policyFields, setPolicyFields] = useState<PolicyField[]>([]);
  const policiesOptions = useMemo(() => availablePolicies?.map(policy => ({ label: policy.name, value: policy.id })), [availablePolicies]);

  useEffect(() => {
    if (policy) {
      setPolicyFields(policy.fields);
      const initialFields = policy.fields.map((field) => ({ [field.key]: field.type === "date" ? dayjs(field.value) : field.value }));
      form.setFieldValue('policy', policy.policy);
      form.setFieldValue('fields', initialFields);
    }
  }, [policy, form]);

  const handleFormSubmit = useCallback((values: Record<string, any>) => {
    const fields = values.fields.map((field: any) => {
      const [key, value] = Object.entries(field)[0];
      const found = policyFields.find(p => p.key === key)!;
      return { ...found, value: value };
    });
    onSubmit?.({ policy: values.policy, fields });
  }, [onSubmit, policyFields]);

  const handlePolicyChange = useCallback((value: string) => {
    if (value) {
      const found = availablePolicies!.find(p => p.id === value)!;
      setPolicyFields(found.fields);
      form.setFieldValue('fields', [...new Array(found.fields.length).map(i => ({ name: i, key: i }))]);
    }
    else setPolicyFields([]);
  }, [availablePolicies, form]);

  return (
    <Form form={form} {...formLayout} initialValues={{ fields: undefined }} onFinish={handleFormSubmit}>
      <Form.Item
        label="Policy"
        name="policy"
        className="mt-4"
        rules={[{
          required: true,
          message: 'Policy is required.'
        }]}
      >
        <Select
          className="w-100"
          placeholder="Policy"
          options={policiesOptions}
          loading={!policiesOptions}
          onChange={handlePolicyChange}
        />
      </Form.Item>

      <Form.List name="fields">
        {(fields) => (
          <>
            {fields.map((_, index) => {
              const field = policyFields[index];

              return (
                <Form.Item
                  key={_.key}
                  name={[_.key, field.key]}
                  required={true}
                  label={field.label}
                  className="mt-4"
                  rules={[{
                    required: true,
                    message: `Policy field ${field.label} is required.`
                  }]}
                >
                  {field.type === "text" && (
                    <Input placeholder={field.label} type="text" />
                  )}
                  {field.type === "date" && (
                    <DatePicker className="w-100" />
                  )}
                  {field.type === "select" && (
                    <Select
                      className="w-100"
                      placeholder={field.label}
                      options={field.values || []}
                    />
                  )}
                </Form.Item>
              )
            })}
          </>
        )}
      </Form.List>
      <div className="d-flex justify-content-between px-3 pb-3">
        <Button variant="light" size="sm" className="border" type="button" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="success" size="sm">
          <FontAwesomeIcon icon={solid('check')} />
          <span className="ms-2">Confirm</span>
        </Button>
      </div>
    </Form>
  )
};